<template>
  <v-layout align-start>
    <v-flex>
      <v-container fluid>
        <!-- Header -->
        <div class="d-flex align-center mb-6">
          <div>
            <h1 class="text-h5 font-weight-bold">Gestión de Roles</h1>
            <p class="grey--text text--darken-1 mb-0 text-body-2">Asigna y administra los roles de la comunidad.</p>
          </div>
          <v-spacer></v-spacer>
          <v-chip class="mr-2" color="primary" outlined>
            <v-icon left small>groups</v-icon>
            {{ communityMembers.length }} miembros
          </v-chip>
        </div>

        <!-- Main tabs -->
        <v-tabs v-model="mainTab" color="primary" class="rounded-t-lg" slider-size="3">
          <v-tab>
            <v-icon left small>people</v-icon>
            Roles
          </v-tab>
          <v-tab>
            <v-icon left small>person_pin</v-icon>
            Referentes
          </v-tab>
          <v-tab v-if="esSuperAdmin">
            <v-icon left small>history</v-icon>
            Historial
          </v-tab>
        </v-tabs>

        <v-divider></v-divider>

        <v-tabs-items v-model="mainTab">
          <!-- ═══════ TAB ROLES ═══════ -->
          <v-tab-item>
            <!-- Role chips filter + search bar -->
            <v-card flat class="pa-4">
              <div class="d-flex flex-wrap align-center mb-4" style="gap: 8px;">
                <v-chip
                  v-for="role in communityRoles"
                  :key="'chip-' + role"
                  :color="roleTab === role ? getRoleColor(role) : 'grey lighten-3'"
                  :dark="roleTab === role"
                  :text-color="roleTab === role ? 'white' : 'grey darken-2'"
                  @click="roleTab = role"
                  class="role-chip font-weight-medium"
                >
                  {{ role }}
                  <v-avatar right class="ml-1" :color="roleTab === role ? 'rgba(255,255,255,0.25)' : 'grey lighten-1'" size="24">
                    <span class="text-caption font-weight-bold" :class="roleTab === role ? 'white--text' : 'grey--text text--darken-2'">
                      {{ (usersByRole[role] || []).length }}
                    </span>
                  </v-avatar>
                </v-chip>
                <v-chip
                  :color="roleTab === 'todos' ? 'primary' : 'grey lighten-3'"
                  :dark="roleTab === 'todos'"
                  :text-color="roleTab === 'todos' ? 'white' : 'grey darken-2'"
                  @click="roleTab = 'todos'"
                  class="role-chip font-weight-medium"
                >
                  Todos
                  <v-avatar right class="ml-1" :color="roleTab === 'todos' ? 'rgba(255,255,255,0.25)' : 'grey lighten-1'" size="24">
                    <span class="text-caption font-weight-bold" :class="roleTab === 'todos' ? 'white--text' : 'grey--text text--darken-2'">
                      {{ communityMembers.length }}
                    </span>
                  </v-avatar>
                </v-chip>
              </div>

              <div class="d-flex align-center mb-2" style="gap: 12px;">
                <v-text-field
                  v-model="roleSearch"
                  prepend-inner-icon="search"
                  label="Buscar por nombre, apellido o email..."
                  single-line
                  hide-details
                  clearable
                  outlined
                  dense
                  style="max-width: 400px;"
                ></v-text-field>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="openAssignDialog" depressed>
                  <v-icon left small>person_add</v-icon>Asignar rol
                </v-btn>
              </div>
            </v-card>

            <!-- Data table -->
            <v-data-table
              :headers="roleHeaders"
              :items="currentRoleList"
              :search="roleSearch"
              :loading="loadingUsers"
              class="elevation-0 rounded-b-lg"
              :items-per-page="15"
              :footer-props="{ 'items-per-page-options': [10, 15, 25, 50] }"
            >
              <template v-slot:item.name="{ item }">
                <div class="d-flex align-center py-2">
                  <v-avatar size="32" :color="getAvatarColor(item.name)" class="mr-3">
                    <span class="white--text text-caption font-weight-bold">{{ getInitials(item) }}</span>
                  </v-avatar>
                  <div>
                    <div class="font-weight-medium">{{ item.name }} {{ item.surName }}</div>
                    <div class="grey--text text-caption">{{ item.email }}</div>
                  </div>
                </div>
              </template>
              <template v-slot:item.rol="{ item }">
                <v-chip
                  v-for="role in parseCommunityRoles(item.rol)"
                  :key="role"
                  :color="getRoleColor(role)"
                  dark
                  x-small
                  class="mr-1"
                >{{ role }}</v-chip>
              </template>
              <template v-slot:item.active="{ item }">
                <v-chip x-small :color="item.active ? 'rgba(129,199,132,0.18)' : 'rgba(207,102,121,0.18)'" :text-color="item.active ? '#a5d6a7' : '#ff8a9b'">
                  {{ item.active ? 'Activo' : 'Inactivo' }}
                </v-chip>
              </template>
              <template v-slot:item.acciones="{ item }">
                <v-btn icon x-small class="mr-1" @click="openEditRolesDialog(item)">
                  <v-icon small color="grey darken-1">edit</v-icon>
                </v-btn>
                <v-btn icon x-small @click="confirmRemoveRole(item)">
                  <v-icon small color="red lighten-1">person_remove</v-icon>
                </v-btn>
              </template>
              <template v-slot:no-data>
                <div class="text-center py-8">
                  <v-icon size="48" color="grey darken-1" class="mb-3">group_off</v-icon>
                  <p class="grey--text text--darken-1 mb-0">No hay miembros con este rol.</p>
                </div>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- ═══════ TAB REFERENTES (ABM) ═══════ -->
          <v-tab-item>
            <v-card flat class="pa-4">
              <div class="d-flex align-center mb-2" style="gap: 12px;">
                <v-text-field
                  v-model="refSearch"
                  prepend-inner-icon="search"
                  label="Buscar referente..."
                  single-line
                  hide-details
                  clearable
                  outlined
                  dense
                  style="max-width: 350px;"
                ></v-text-field>
                <v-select
                  v-model="refFilterDept"
                  :items="departmentOptions"
                  label="Departamento"
                  clearable
                  dense
                  outlined
                  hide-details
                  style="max-width: 200px;"
                ></v-select>
                <v-spacer></v-spacer>
                <v-btn color="primary" depressed @click="openRefDialog()">
                  <v-icon left small>add</v-icon>Nuevo Referente
                </v-btn>
              </div>
            </v-card>

            <v-data-table
              :headers="refHeaders"
              :items="filteredReferents"
              :search="refSearch"
              :loading="loadingReferents"
              class="elevation-0"
              :items-per-page="15"
              :footer-props="{ 'items-per-page-options': [10, 15, 25, 50] }"
            >
              <template v-slot:item.fullName="{ item }">
                <div class="d-flex align-center py-2">
                  <v-avatar size="32" :color="getAvatarColor(item.name)" class="mr-3">
                    <span class="white--text text-caption font-weight-bold">{{ item.name ? item.name[0] : '' }}{{ item.surName ? item.surName[0] : '' }}</span>
                  </v-avatar>
                  <span class="font-weight-medium">{{ item.name }} {{ item.surName }}</span>
                </div>
              </template>
              <template v-slot:item.active="{ item }">
                <v-chip x-small :color="item.active ? 'rgba(129,199,132,0.18)' : 'rgba(207,102,121,0.18)'" :text-color="item.active ? '#a5d6a7' : '#ff8a9b'">
                  {{ item.active ? 'Activo' : 'Inactivo' }}
                </v-chip>
              </template>
              <template v-slot:item.headquarter="{ item }">
                {{ item.headquarter ? item.headquarter.name : '-' }}
              </template>
              <template v-slot:item.opciones="{ item }">
                <v-btn icon x-small class="mr-1" @click="openRefDialog(item)">
                  <v-icon small color="grey darken-1">edit</v-icon>
                </v-btn>
                <v-btn icon x-small class="mr-1" @click="item.active ? deactivateReferent(item._id) : activateReferent(item._id)">
                  <v-icon small :color="item.active ? 'orange darken-1' : 'green'">
                    {{ item.active ? 'visibility_off' : 'visibility' }}
                  </v-icon>
                </v-btn>
                <v-btn icon x-small @click="confirmDeleteRef(item)">
                  <v-icon small color="red lighten-1">delete</v-icon>
                </v-btn>
              </template>
              <template v-slot:no-data>
                <div class="text-center py-8">
                  <v-icon size="48" color="grey darken-1" class="mb-3">person_pin</v-icon>
                  <p class="grey--text text--darken-1 mb-1">No hay referentes registrados.</p>
                  <v-btn color="primary" small depressed @click="openRefDialog()">Crear referente</v-btn>
                </div>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- ═══════ TAB HISTORIAL (solo Super Admin) ═══════ -->
          <v-tab-item v-if="esSuperAdmin">
            <v-card flat class="pa-4">
              <div class="d-flex align-center" style="gap: 12px;">
                <v-text-field
                  v-model="logSearch"
                  prepend-inner-icon="search"
                  label="Buscar en historial..."
                  single-line
                  hide-details
                  clearable
                  outlined
                  dense
                  style="max-width: 400px;"
                ></v-text-field>
                <v-spacer></v-spacer>
                <v-btn icon @click="loadLogs" :loading="loadingLogs">
                  <v-icon>refresh</v-icon>
                </v-btn>
              </div>
            </v-card>

            <v-data-table
              :headers="logHeaders"
              :items="roleLogs"
              :search="logSearch"
              :loading="loadingLogs"
              class="elevation-0"
              :items-per-page="20"
              :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
            >
              <template v-slot:item.createdAt="{ item }">
                <span class="text-caption">{{ formatDate(item.createdAt) }}</span>
              </template>
              <template v-slot:item.previousRoles="{ item }">
                <v-chip
                  v-for="r in parseJsonRoles(item.previousRoles)"
                  :key="'prev-' + r"
                  :color="getRoleColor(r) || 'grey'"
                  dark x-small class="mr-1"
                >{{ r }}</v-chip>
                <span v-if="parseJsonRoles(item.previousRoles).length === 0" class="grey--text">-</span>
              </template>
              <template v-slot:item.newRoles="{ item }">
                <v-chip
                  v-for="r in parseJsonRoles(item.newRoles)"
                  :key="'new-' + r"
                  :color="getRoleColor(r) || 'grey'"
                  dark x-small class="mr-1"
                >{{ r }}</v-chip>
                <span v-if="parseJsonRoles(item.newRoles).length === 0" class="grey--text">-</span>
              </template>
              <template v-slot:item.changedByName="{ item }">
                <v-icon small class="mr-1">admin_panel_settings</v-icon>
                {{ item.changedByName }}
              </template>
              <template v-slot:no-data>
                <div class="text-center py-8">
                  <v-icon size="48" color="grey darken-1" class="mb-3">history</v-icon>
                  <p class="grey--text text--darken-1 mb-0">No hay registros de cambios.</p>
                </div>
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </v-container>

      <!-- ═══════ DIALOGS ═══════ -->

      <!-- Asignar rol de comunidad -->
      <v-dialog v-model="assignDialog" max-width="550px" scrollable>
        <v-card class="rounded-lg">
          <v-card-title class="headline primary white--text py-3">
            <v-icon dark class="mr-2">person_add</v-icon>
            Asignar rol
          </v-card-title>
          <v-card-text class="pt-5" style="overflow: visible;">
            <p class="mb-2 font-weight-medium text-body-2">USUARIO</p>
            <v-autocomplete
              v-model="assignForm.userId"
              :items="availableUsers"
              :search-input.sync="userSearch"
              item-text="displayName"
              item-value="_id"
              label="Buscar usuario"
              placeholder="Nombre, apellido o email..."
              outlined dense clearable
              no-data-text="No se encontraron usuarios"
              :menu-props="{ maxHeight: 200, offsetY: true }"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }} {{ item.surName }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip v-for="r in parseCommunityRoles(item.rol)" :key="r" :color="getRoleColor(r)" dark x-small class="ml-1">{{ r }}</v-chip>
                </v-list-item-action>
              </template>
            </v-autocomplete>
            <p class="mb-2 font-weight-medium text-body-2">ROLES A ASIGNAR</p>
            <v-checkbox v-for="role in communityRoles" :key="'assign-' + role" v-model="assignForm.roles" :label="role" :value="role" :color="getRoleColor(role)" hide-details dense class="mb-1"></v-checkbox>
            <v-alert v-if="assignForm.roles.length === 0" type="info" dense text class="mt-3">Selecciona al menos un rol.</v-alert>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text @click="assignDialog = false">Cancelar</v-btn>
            <v-btn color="primary" depressed @click="confirmAssign" :disabled="!assignForm.userId || assignForm.roles.length === 0" :loading="saving">Asignar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Editar roles de usuario -->
      <v-dialog v-model="editRolesDialog" max-width="500px">
        <v-card class="rounded-lg">
          <v-card-title class="headline primary white--text py-3">
            <v-icon dark class="mr-2">edit</v-icon>
            Roles de {{ editRolesForm.userName }}
          </v-card-title>
          <v-card-text class="pt-5">
            <v-checkbox v-for="role in communityRoles" :key="'edit-' + role" v-model="editRolesForm.roles" :label="role" :value="role" :color="getRoleColor(role)" hide-details dense class="mb-1"></v-checkbox>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text @click="editRolesDialog = false">Cancelar</v-btn>
            <v-btn color="primary" depressed @click="confirmEditRoles" :loading="saving">Guardar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Confirmar quitar rol -->
      <v-dialog v-model="removeRoleDialog" max-width="400px">
        <v-card class="rounded-lg">
          <v-card-title class="headline">Confirmar</v-card-title>
          <v-card-text>
            ¿Quitar el rol <strong>{{ removeRoleTarget.role }}</strong> a <strong>{{ removeRoleTarget.userName }}</strong>?
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text @click="removeRoleDialog = false">Cancelar</v-btn>
            <v-btn color="error" depressed @click="executeRemoveRole" :loading="saving">Quitar rol</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog ABM Referente -->
      <v-dialog v-model="refDialog" max-width="700px" persistent>
        <v-card class="rounded-lg">
          <v-card-title class="headline primary white--text py-3">
            <v-icon dark class="mr-2">{{ refIsEditing ? 'edit' : 'add' }}</v-icon>
            {{ refIsEditing ? 'Editar Referente' : 'Nuevo Referente' }}
          </v-card-title>
          <v-card-text class="pt-5">
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field v-model="refForm.name" label="Nombre *" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="refForm.surName" label="Apellido *" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="refForm.role" label="Cargo / Rol" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="refForm.department" :items="departments" label="Departamento" outlined dense clearable></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="refForm.area" label="Area / Zona" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="refForm.phone" label="Telefono" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="refForm.email" label="Email" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="refForm.headquarterId"
                  :items="headquarterOptions"
                  item-text="name"
                  item-value="_id"
                  label="Sede"
                  outlined dense clearable
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="refForm.bio" label="Biografia" outlined dense rows="3"></v-textarea>
              </v-col>
            </v-row>

            <v-divider class="mb-3"></v-divider>
            <p class="font-weight-medium mb-2 text-body-2">Visibilidad en la web:</p>
            <v-row>
              <v-col cols="6" sm="4">
                <v-switch v-model="refForm.showPhone" label="Telefono" dense hide-details class="mt-0"></v-switch>
              </v-col>
              <v-col cols="6" sm="4">
                <v-switch v-model="refForm.showEmail" label="Email" dense hide-details class="mt-0"></v-switch>
              </v-col>
              <v-col cols="6" sm="4">
                <v-switch v-model="refForm.showBio" label="Biografia" dense hide-details class="mt-0"></v-switch>
              </v-col>
              <v-col cols="6" sm="4">
                <v-switch v-model="refForm.showRole" label="Cargo" dense hide-details class="mt-0"></v-switch>
              </v-col>
              <v-col cols="6" sm="4">
                <v-switch v-model="refForm.showDepartment" label="Departamento" dense hide-details class="mt-0"></v-switch>
              </v-col>
              <v-col cols="6" sm="4">
                <v-switch v-model="refForm.showArea" label="Area" dense hide-details class="mt-0"></v-switch>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text @click="refDialog = false">Cancelar</v-btn>
            <v-btn color="primary" depressed @click="saveRef" :disabled="!refForm.name || !refForm.surName">
              {{ refIsEditing ? 'Guardar' : 'Crear' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapState, mapActions } from "vuex";
import axios from "axios";
import swal from "sweetalert";

const EXCLUDED_ROLES = ["Super Admin", "Administrador", "usuario"];
let COMMUNITY_ROLES = ["Embajador", "Referente"];

const DEPARTMENTS = [
  "Artigas", "Canelones", "Cerro Largo", "Colonia", "Durazno",
  "Flores", "Florida", "Lavalleja", "Maldonado", "Montevideo",
  "Paysandu", "Rio Negro", "Rivera", "Rocha", "Salto",
  "San Jose", "Soriano", "Tacuarembo", "Treinta y Tres",
];

const AVATAR_COLORS = [
  "#5C6BC0", "#42A5F5", "#26A69A", "#66BB6A", "#FFA726",
  "#EF5350", "#AB47BC", "#8D6E63", "#78909C", "#EC407A",
];

export default {
  name: "CommunityRoles",
  data() {
    return {
      mainTab: 0,
      saving: false,
      communityRoles: [],

      // Roles tab
      roleSearch: "",
      roleTab: null,
      loadingUsers: false,
      allUsers: [],
      userSearch: "",
      assignDialog: false,
      assignForm: { userId: null, roles: [] },
      editRolesDialog: false,
      editRolesForm: { userId: "", userName: "", allRoles: [], roles: [] },
      removeRoleDialog: false,
      removeRoleTarget: { userId: "", userName: "", role: "", allRoles: [] },
      roleHeaders: [
        { text: "Miembro", value: "name", sortable: true },
        { text: "Roles", value: "rol", sortable: false },
        { text: "Estado", value: "active", sortable: true, width: "100px" },
        { text: "Acciones", value: "acciones", sortable: false, width: "100px" },
      ],

      // Referentes ABM tab
      refSearch: "",
      refFilterDept: null,
      loadingReferents: false,
      refDialog: false,
      refIsEditing: false,
      refEditingId: null,
      refForm: this.emptyRefForm(),
      departments: DEPARTMENTS,
      refHeaders: [
        { text: "Opciones", value: "opciones", sortable: false, width: "120px" },
        { text: "Nombre completo", value: "fullName", sortable: true },
        { text: "Cargo", value: "role", sortable: true },
        { text: "Departamento", value: "department", sortable: true },
        { text: "Area", value: "area", sortable: true },
        { text: "Email", value: "email", sortable: false },
        { text: "Telefono", value: "phone", sortable: false },
        { text: "Sede", value: "headquarter", sortable: true },
        { text: "Estado", value: "active", sortable: true },
      ],

      // Historial
      logSearch: "",
      loadingLogs: false,
      roleLogs: [],
      logHeaders: [
        { text: "Fecha", value: "createdAt", sortable: true, width: "170px" },
        { text: "Usuario afectado", value: "userName", sortable: true },
        { text: "Roles anteriores", value: "previousRoles", sortable: false },
        { text: "Roles nuevos", value: "newRoles", sortable: false },
        { text: "Responsable", value: "changedByName", sortable: true },
      ],
    };
  },
  watch: {
    'assignForm.userId'(userId) {
      if (!userId) return;
      const user = this.allUsers.find((u) => u._id === userId);
      if (user) {
        const currentCommunityRoles = this.parseRoles(user.rol).filter((r) => this.communityRoles.includes(r));
        this.assignForm.roles = [...currentCommunityRoles];
      }
    },
  },
  async created() {
    await this.loadCommunityRoles();
    this.loadUsers();
    this.getReferents();
    this.getHeadquarters();
    if (this.esSuperAdmin) this.loadLogs();
  },
  computed: {
    ...mapState("userNamespace", ["token", "user"]),
    ...mapState("referentNamespace", ["referents"]),
    ...mapState("headquarterNamespace", ["headquarters"]),

    usersByRole() {
      const map = {};
      for (const role of this.communityRoles) {
        map[role] = this.allUsers.filter((u) => this.parseRoles(u.rol).includes(role));
      }
      return map;
    },
    communityMembers() {
      return this.allUsers.filter((u) => {
        const roles = this.parseRoles(u.rol);
        return roles.some((r) => this.communityRoles.includes(r));
      });
    },
    currentRoleList() {
      if (this.roleTab && this.usersByRole[this.roleTab]) return this.usersByRole[this.roleTab];
      return this.communityMembers;
    },
    availableUsers() {
      return this.allUsers.map((u) => ({
        ...u,
        displayName: `${u.name} ${u.surName} (${u.email})`,
      }));
    },
    activeReferents() {
      return this.referents.filter((r) => r.active);
    },
    filteredReferents() {
      if (!this.refFilterDept) return this.referents;
      return this.referents.filter((r) => r.department === this.refFilterDept);
    },
    departmentOptions() {
      const deps = new Set(this.referents.map((r) => r.department).filter(Boolean));
      return [...deps].sort();
    },
    headquarterOptions() {
      return this.headquarters || [];
    },
    esSuperAdmin() {
      if (!this.user || !this.user.user) return false;
      const role = this.user.user.role;
      if (Array.isArray(role)) return role.includes("Super Admin");
      try { const p = JSON.parse(role); return Array.isArray(p) ? p.includes("Super Admin") : role === "Super Admin"; } catch { return role === "Super Admin"; }
    },
  },
  methods: {
    ...mapActions("userNamespace", ["getUsers", "updateUser"]),
    ...mapActions("referentNamespace", [
      "getReferents", "saveReferent", "updateReferent",
      "activateReferent", "deactivateReferent", "deleteReferent",
    ]),
    ...mapActions("headquarterNamespace", ["getHeadquarters"]),

    // Helpers
    parseRoles(rol) {
      if (!rol) return [];
      if (Array.isArray(rol)) return rol;
      try {
        const parsed = JSON.parse(rol);
        return Array.isArray(parsed) ? parsed : [rol];
      } catch { return [rol]; }
    },
    parseCommunityRoles(rol) {
      return this.parseRoles(rol).filter((r) => this.communityRoles.includes(r));
    },
    getRoleColor(role) {
      const colors = [
        "#5C6BC0", "#E91E63", "#009688",
        "#FF7043", "#7E57C2", "#795548",
        "#EC407A", "#607D8B", "#43A047", "#F9A825",
      ];
      const idx = this.communityRoles.indexOf(role);
      return idx >= 0 ? colors[idx % colors.length] : "grey";
    },
    getInitials(item) {
      return `${item.name ? item.name[0] : ''}${item.surName ? item.surName[0] : ''}`.toUpperCase();
    },
    getAvatarColor(name) {
      if (!name) return AVATAR_COLORS[0];
      const idx = name.charCodeAt(0) % AVATAR_COLORS.length;
      return AVATAR_COLORS[idx];
    },
    emptyRefForm() {
      return {
        name: "", surName: "", role: "", department: "", area: "",
        phone: "", email: "", bio: "", headquarterId: null,
        showPhone: true, showEmail: true, showBio: true,
        showRole: true, showDepartment: true, showArea: true,
      };
    },

    // Roles: Load
    async loadCommunityRoles() {
      try {
        const { data } = await axios.get("admin/roles", {
          headers: { Authorization: this.token },
        });
        const roles = data
          .map((r) => r.key)
          .filter((r) => !EXCLUDED_ROLES.includes(r));
        this.communityRoles = roles;
        COMMUNITY_ROLES = roles;
        if (roles.length > 0 && !this.roleTab) this.roleTab = roles[0];
      } catch (e) {
        console.error("Error loading roles:", e);
        this.communityRoles = ["Embajador", "Referente"];
        COMMUNITY_ROLES = this.communityRoles;
        this.roleTab = "Embajador";
      }
    },
    async loadUsers() {
      this.loadingUsers = true;
      try {
        await this.getUsers();
        this.allUsers = this.$store.state.userNamespace.users || [];
      } catch (e) {
        console.error("Error loading users:", e);
      } finally {
        this.loadingUsers = false;
      }
    },

    // Roles: Assign
    openAssignDialog() {
      this.assignForm = { userId: null, roles: [] };
      if (this.roleTab !== "todos") this.assignForm.roles = [this.roleTab];
      this.assignDialog = true;
    },
    async confirmAssign() {
      if (!this.assignForm.userId || this.assignForm.roles.length === 0) return;
      this.saving = true;
      try {
        const user = this.allUsers.find((u) => u._id === this.assignForm.userId);
        const currentRoles = this.parseRoles(user.rol);
        const newRoles = [...new Set([...currentRoles, ...this.assignForm.roles])];
        await this.updateUser({ id: user._id, rol: JSON.stringify(newRoles) });
        this.assignDialog = false;
        await this.loadUsers();
        swal({ title: "Rol asignado", text: `Roles asignados: ${this.assignForm.roles.join(", ")}`, icon: "success" });
      } catch (e) {
        swal({ title: "Error", text: "No se pudo asignar el rol", icon: "error" });
      } finally { this.saving = false; }
    },

    // Roles: Edit
    openEditRolesDialog(user) {
      const allRoles = this.parseRoles(user.rol);
      this.editRolesForm = {
        userId: user._id,
        userName: `${user.name} ${user.surName}`,
        allRoles,
        roles: allRoles.filter((r) => this.communityRoles.includes(r)),
      };
      this.editRolesDialog = true;
    },
    async confirmEditRoles() {
      this.saving = true;
      try {
        const nonCommunity = this.editRolesForm.allRoles.filter((r) => !this.communityRoles.includes(r));
        const newRoles = [...nonCommunity, ...this.editRolesForm.roles];
        if (newRoles.length === 0) newRoles.push("Affiliate");
        await this.updateUser({ id: this.editRolesForm.userId, rol: JSON.stringify(newRoles) });
        this.editRolesDialog = false;
        await this.loadUsers();
        swal({ title: "Roles actualizados", text: `${this.editRolesForm.userName} actualizado`, icon: "success" });
      } catch (e) {
        swal({ title: "Error", text: "No se pudieron actualizar los roles", icon: "error" });
      } finally { this.saving = false; }
    },

    // Roles: Remove
    confirmRemoveRole(user) {
      const allRoles = this.parseRoles(user.rol);
      const role = this.roleTab !== "todos" ? this.roleTab : null;
      if (!role) { this.openEditRolesDialog(user); return; }
      this.removeRoleTarget = { userId: user._id, userName: `${user.name} ${user.surName}`, role, allRoles };
      this.removeRoleDialog = true;
    },
    async executeRemoveRole() {
      this.saving = true;
      try {
        const newRoles = this.removeRoleTarget.allRoles.filter((r) => r !== this.removeRoleTarget.role);
        if (newRoles.length === 0) newRoles.push("Affiliate");
        await this.updateUser({ id: this.removeRoleTarget.userId, rol: JSON.stringify(newRoles) });
        this.removeRoleDialog = false;
        await this.loadUsers();
        swal({ title: "Rol removido", text: `Se quito ${this.removeRoleTarget.role} a ${this.removeRoleTarget.userName}`, icon: "success" });
      } catch (e) {
        swal({ title: "Error", text: "No se pudo quitar el rol", icon: "error" });
      } finally { this.saving = false; }
    },

    // Referentes ABM
    openRefDialog(item) {
      if (item) {
        this.refIsEditing = true;
        this.refEditingId = item._id;
        this.refForm = {
          name: item.name || "",
          surName: item.surName || "",
          role: item.role || "",
          department: item.department || "",
          area: item.area || "",
          phone: item.phone || "",
          email: item.email || "",
          bio: item.bio || "",
          headquarterId: item.headquarterId || null,
          showPhone: item.showPhone !== false,
          showEmail: item.showEmail !== false,
          showBio: item.showBio !== false,
          showRole: item.showRole !== false,
          showDepartment: item.showDepartment !== false,
          showArea: item.showArea !== false,
        };
      } else {
        this.refIsEditing = false;
        this.refEditingId = null;
        this.refForm = this.emptyRefForm();
      }
      this.refDialog = true;
    },
    async saveRef() {
      if (this.refIsEditing) {
        await this.updateReferent({ ...this.refForm, _id: this.refEditingId });
      } else {
        await this.saveReferent(this.refForm);
      }
      this.refDialog = false;
    },
    confirmDeleteRef(item) {
      swal({
        title: "Estas seguro?",
        text: `Se eliminara el referente "${item.name} ${item.surName}".`,
        icon: "warning",
        buttons: ["Cancelar", "Eliminar"],
        dangerMode: true,
      }).then((willDelete) => {
        if (willDelete) this.deleteReferent(item._id);
      });
    },

    // Historial
    async loadLogs() {
      this.loadingLogs = true;
      try {
        const token = localStorage.getItem("token");
        const config = { headers: { Authorization: token } };
        const { data } = await axios.get("admin/role-change-logs?limit=200", config);
        this.roleLogs = data.logs || [];
      } catch (e) {
        console.error("Error loading role change logs:", e);
      } finally {
        this.loadingLogs = false;
      }
    },
    parseJsonRoles(str) {
      if (!str) return [];
      try {
        const parsed = JSON.parse(str);
        return Array.isArray(parsed) ? parsed : [str];
      } catch { return [str]; }
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      const d = new Date(dateStr);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
  },
};
</script>

<style scoped>
.role-chip {
  cursor: pointer;
  transition: all 0.2s ease;
}
.role-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
</style>
